<template>
  <div class="relative w-full flex flex-col">
    <!-- Search Bar -->
    <div v-if="!disableSearch" class="w-full flex items-center pl-2.5 pr-1 p-1.5 gap-2">
      <div class="w-5 h-5 flex-shrink-0 flex items-center justify-center">
        <SearchIcon class="text-icon-normal" :width="18" :height="18" />
      </div>
      <input v-model="searchQuery" ref="searchInput" class="search-input flex-grow" placeholder="Search Ad Accounts..." />
    </div>
    <div v-if="!disableSearch" class="fade-overlay" />
    <div class="accounts-list w-full flex flex-col gap-1 transition-opacity duration-200"
    :class="[{'opacity-60 pointer-events-none': loading}, listPaddingClasses]" 
    :style="{maxHeight: `${maxListHeight}px`}">
      <!-- No results state -->
      <div v-if="!searchedAdAccounts.length" class="w-full px-2 py-1">
        <BaseText type="body" size="xs" class="text-text-subdued">
          No results match your search
        </BaseText>
      </div>
      <!-- Accounts List -->
      <div v-for="account in searchedAdAccounts" :key="`${account.platform}-account-${account.id}`"
      class="w-full flex items-center p-1 transition duration-100"
      :class="{'opacity-50': previewDeselect(account)}">
        <button class="w-6 h-6 flex items-center justify-center flex-shrink-0"
        @mouseenter="hoveredPlatform = account.platform" @mouseleave="hoveredPlatform = null"
        @click="handleSelect(account)" :disabled="isAccountDisabled(account)">
          <input type="checkbox" class="checkbox-input rounded text-black cursor-pointer focus:ring-transparent transition-colors"
          :checked="isAccountSelected(account) && !previewDeselect(account)" :disabled="isAccountDisabled(account) || previewDeselect(account)" />
        </button>
        <!-- Wrapping Details -->
        <div class="flex-grow min-w-0 flex items-center gap-x-2 gap-y-0.5 flex-wrap ml-2 select-none">
          <!-- Platform Icon -->
          <div v-if="showPlatformIcons" class="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <component :is="getPlatformIcon(account.platform)" class="text-icon-normal" :width="16" :height="16" />
          </div>
          <!-- Account Name -->
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ account.name }}
          </BaseText>
          <!-- Business Name -->
          <BaseText type="label" size="xs" class="text-text-muted px-1 py-0.5 rounded-md bg-neutral-25">
            {{ account.companyName }}
          </BaseText>
          <!-- Account ID -->
          <BaseText type="body" size="xs" class="text-text-subdued">
            {{ formatAccountId(account) }}
          </BaseText>
        </div>
        <!-- Curreny Indicator -->
        <div class="flex items-center px-1.5 py-1 rounded-md bg-neutral-25 flex-shrink-0 whitespace-nowrap ml-2">
          <BaseText type="body" size="xs" class="text-text-normal text-center w-2.5">
            {{ getCurrencySymbol(account.currency) }}
          </BaseText>
          <div class="w-0 h-3 mx-1.5 border-l border-border-normal" />
          <BaseText type="body" size="xs" class="text-text-normal uppercase">
            {{ account.currency }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import MetaPlatformIcon from '../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'

export default {
  name: 'AdAccountMultiSelector',
  components: {
    SearchIcon
  },
  props: {
    adAccountOptions: {
      type: Array,
      required: true
    },
    selectedAccountIds: {
      type: Array,
      required: true
    },
    disabledAccountIds: {
      type: Array,
      default: () => []
    },
    showPlatformIcons: {
      type: Boolean,
      default: false
    },
    disableSearch: {
      type: Boolean,
      default: false
    },
    listPaddingClasses: {
      type: String,
      default: 'px-1 pt-1 pb-3'
    },
    loading: {
      type: Boolean,
      default: false
    },
    maxListHeight: {
      type: Number,
      default: 222
    },
    usePlatformExclusiveSelectUI: {
      type: Boolean,
      default: false
    },
    currentSelectionPlatform: {
      // Used in conjunction with usePlatformExclusiveSelectUI to determine if the account should be previewed for deselection
      type: String,
      default: null
    }
  },
  data () {
    return {
      searchQuery: '',
      hoveredPlatform: null
    }
  },
  watch: {
    searchedAdAccounts () {
      this.$emit('reflow')
    }
  },
  computed: {
    searchedAdAccounts () {
      if (!this.searchQuery.length || !this.adAccountOptions.length) return this.adAccountOptions
      let searchItems = this.adAccountOptions.filter(account => account.name.toLowerCase().includes(this.searchQuery.toLowerCase()))

      // Sort by the closeness of the match (closer = lower index)
      searchItems.sort((a, b) => {
        const ratioA = this.searchQuery.length / a.name.length;
        const ratioB = this.searchQuery.length / b.name.length;

        return ratioB - ratioA;
      });

      return searchItems;
    }
  },
  methods: {
    handleSelect (account) {
      this.$emit('select', account)
    },
    isAccountSelected (account) {
      return this.selectedAccountIds.some(accountId => accountId === account.id)
    },
    isAccountDisabled (account) {
      return this.disabledAccountIds.some(accountId => accountId === account.id)
    },
    previewDeselect (account) {
      if (!this.usePlatformExclusiveSelectUI || !this.hoveredPlatform || !this.currentSelectionPlatform) return false
      return account.platform === this.currentSelectionPlatform && this.hoveredPlatform !== this.currentSelectionPlatform
    },
    formatAccountId (account) {
      const id = account.id
      return account.platform === 'meta' ? id.slice(4) : id
    },
    getCurrencySymbol (currency) {
      return new Intl.NumberFormat('en', { 
        style: 'currency', 
        currency: currency?.length ? currency : 'USD',
        currencyDisplay: 'symbol'
      }).format(0).charAt(0)
    },
    getPlatformIcon (platform) {
      return platform === 'meta' ? MetaPlatformIcon : TikTokPlatformIcon
    }
  }
}
</script>

<style scoped>
.accounts-list {
  overflow-y: auto;
}
.accounts-list::-webkit-scrollbar {
  width: 5px;
}
.accounts-list::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}
.fade-overlay {
  position: absolute;
  top: 32px;
  left: 0;
  right: 5px;
  height: 8px;
  background: linear-gradient(to bottom, white, transparent);
  z-index: 10;
  pointer-events: none;
}

/* INPUT STYLING */

.search-input {
  /* Reset default styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  color: inherit;
  background-color: transparent;
  outline: none;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.search-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.search-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.search-input:hover::placeholder {
  color: #303546;
}
.search-input:focus::placeholder {
  color: #A4ACB9;
}
.checkbox-input {
  border: 1px;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.checkbox-input:focus,
.checkbox-input:focus-visible,
.checkbox-input:active {
  outline: none;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
}
.checkbox-input:disabled, .checkbox-input:disabled:hover {
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.06), 0px 1px 2px 0px rgba(9, 25, 72, 0.06);
  background-color: #ECEFF3;
}
</style>